import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../aws/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL;
const API_URL = `${API_BASE_URL}/v1-proveedores`;
const API_PAGOS_URL = `${API_BASE_URL}/v1-proveedores-pagos`;

export const useGetProveedores = () => {
  const { getJwtToken } = useAuth();
  return useQuery({
    queryKey: ['proveedores'],
    queryFn: async () => {
      const token = await getJwtToken();
      const res = await fetch(API_URL, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Error al cargar proveedores');
      return res.json();
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useSaveProveedor = () => {
  const queryClient = useQueryClient();
  const { getJwtToken } = useAuth();

  return useMutation({
    mutationFn: async (payload) => {
      const token = await getJwtToken();
      const isEdit = !!payload.id;
      const url = isEdit ? `${API_URL}/${payload.id}` : API_URL;

      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detalle || errorData.error || 'Error al procesar proveedor');
      }
      return res.json();
    },
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: ['proveedores'] });
    },
  });
};

export const useSavePagoProveedor = () => {
  const queryClient = useQueryClient();
  const { getJwtToken } = useAuth();

  return useMutation({
    mutationFn: async (payload) => {
      const token = await getJwtToken();

      const res = await fetch(API_PAGOS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detalle || errorData.error || 'Error al procesar el pago');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proveedores'] });
      queryClient.invalidateQueries({ queryKey: ['caja'] });
    },
  });
};

// Añade esto al final de hooks/useProveedores.js
export const useDeleteProveedor = () => {
  const queryClient = useQueryClient();
  const { getJwtToken } = useAuth();

  return useMutation({
    mutationFn: async (id) => {
      const token = await getJwtToken();
      const res = await fetch(`${API_URL}/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detalle || errorData.error || 'Error al eliminar proveedor');
      }
      return res.json();
    },
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: ['proveedores'] });
    },
  });
};

// Añade esto al final de hooks/useProveedores.js
export const useGetPagosProveedor = (proveedorId) => {
  const { getJwtToken } = useAuth();
  return useQuery({
    // La queryKey incluye el ID para que React Query separe el caché por proveedor
    queryKey: ['pagos_proveedor', proveedorId],
    queryFn: async () => {
      if (!proveedorId) return [];
      const token = await getJwtToken();
      // Usamos la misma URL pero por método GET
      const res = await fetch(`${API_BASE_URL}/v1-proveedores-pagos?proveedor_id=${proveedorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al cargar el historial de pagos');
      return res.json();
    },
    enabled: !!proveedorId, // Solo se dispara si el modal está abierto y hay un ID
  });
};